import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, MenuItem, Select, TextField, Typography, styled } from '@mui/material';
import BuildingService from '../../../services/building/BuildingService';
import IAdminPageBuildingDetail from '../../../models/adminPage/adminPageBuildingDetail';
import ModalCard, { ModalRow, ModalTitle } from '../../modal/ModalCard';
import Progress from '../../progress/Progress';
import eslTemplateService from '../../../services/esl/EslTemplateService';
import IEslTemplate, { DEFAULT_ESL_TEMPLATE } from '../../../models/esl/eslTemplate';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../routes';

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  textAlign: 'center',
  marginBottom: 16
}));

interface IAddEslTemplateModalProps {
  isModalOpen: boolean;
  onCloseModal: () => void;
}

const AddEslTemplateModal = ({ isModalOpen, onCloseModal }: IAddEslTemplateModalProps) => {
  const { t } = useTranslation(['eslTemplate', 'common']);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
  const [template, setTemplate] = useState<IEslTemplate>(DEFAULT_ESL_TEMPLATE);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    BuildingService.GetBuildingsDetails().then(setBuildings);
  }, []);

  const onAddEslTemplate = async () => {
    if (!template) return;

    setIsLoading(true);

    try {
      const templateId = await eslTemplateService.CreateEslTemplate(template);
      onCloseModal();
      navigate(routes.dataRoute.eslTemplateEditor, { state: { id: templateId } })
    }
    catch (error) {
      setErrorMessage(t("eslTemplate:createModal.templateCreationError"));
    }
    finally {
      setIsLoading(false);
    }
  };

  const setTemplateBuildings = (buildingIds: number[]) => {
    setTemplate((template) => ({
      ...template,
      buildings: buildingIds.map((id) => ({ id: 0, buildingId: id, eslTemplateId: template.id, isActive: false }))
    }));
  };

  return (
    <Dialog fullWidth PaperProps={{ style: { minWidth: '60vw' } }} open={isModalOpen} onClose={onCloseModal}>
      <ModalCard sx={{ borderRadius: 4 }} title={t('eslTemplate:createModal.createModalTitle')} closeCallback={onCloseModal}>

        <ModalCard.Main isDivider={false}>
          <ModalRow>
            <ModalTitle>{t('eslTemplate:createModal.name')}</ModalTitle>
            <TextField
              required
              style={{ textAlign: 'left', width: '100%' }}
              value={template.name}
              onChange={(e) => {
                setTemplate((template) => ({ ...template, name: e.target.value }));
              }}
            />
          </ModalRow>

          <ModalRow>
            <ModalTitle>{t('eslTemplate:createModal.buildings')}</ModalTitle>
            <Select
              multiple
              value={template.buildings.map((b) => b.buildingId)}
              onChange={(e) => setTemplateBuildings(e.target.value as number[])}
              sx={{ width: '100%', height: '2em' }}
              MenuProps={{ style: { overflowY: 'visible' } }}
            >
              {buildings.map((building) => (
                <MenuItem
                  key={building.id}
                  value={building.id}
                >
                  {building.name}
                </MenuItem>
              ))}
            </Select>
          </ModalRow>

          {errorMessage && (
            <Alert
              severity="error"
              sx={{ marginBottom: "1em" }}
            >
              {errorMessage}
            </Alert>
          )}

        </ModalCard.Main>
        <ModalCard.Action>
          <Progress isLoading={isLoading}>
            <Button
              variant="contained"
              color="primary"
              onClick={onAddEslTemplate}
              disabled={!template.name || !template.buildings.length}
            >
              {t('eslTemplate:createModal.createButton')}
            </Button>
          </Progress>
        </ModalCard.Action>

      </ModalCard>
    </Dialog>
  );
};

export default AddEslTemplateModal;
